import { useEffect, useState } from 'react';
import { API_CACHE_TTL, ApiLookupMissError, fetchCachedGetApi, isAbortError } from '@/utils/apiCache';

interface UseGetArtistProps {
  artistId: string | null;
}

export interface ArtistData {
  artistData?: {
    name: string;
    images: Array<{ url: string }>;
    external_urls: { spotify: string };
    followers: { total: number };
    genres: string[];
  };
  name?: string;
  error?: string;
}

function useGetArtist(
  props: UseGetArtistProps,
  options?: unknown,
  dependencies: unknown[] = []
): ArtistData | null {
  const [artistData, setArtistData] = useState<ArtistData | null>(null);
  const { artistId } = props;

  async function fetchSpotifyArtist(artistId: string, signal?: AbortSignal): Promise<ArtistData> {
    if (typeof window !== 'undefined') {
      return fetchCachedGetApi<ArtistData>("/.netlify/functions/SpotifyGetArtist", { artist: artistId }, {
        ttlMs: API_CACHE_TTL.SIX_HOURS,
        missTtlMs: API_CACHE_TTL.SIX_HOURS,
        shouldCache: (data) => Boolean(data?.artistData && !data.error),
        signal,
      });
    }
    throw new Error('Window is undefined');
  }

  useEffect(() => {
    if (typeof window === 'undefined' || !artistId) return;

    const controller = new AbortController();

    fetchSpotifyArtist(artistId, controller.signal)
      .then((data) => {
        if (controller.signal.aborted) return;
        setArtistData(data);
      })
      .catch((err) => {
        if (controller.signal.aborted || isAbortError(err)) return;
        if (err instanceof ApiLookupMissError) {
          setArtistData({ error: 'Artist not found' });
          return;
        }
        setArtistData({ error: err instanceof Error ? err.message : 'Failed to load artist data' });
      });

    return () => controller.abort();
  }, [...dependencies]);

  if (!artistId) {
    return null;
  }

  return artistData;
}

export default useGetArtist;
